import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ClipboardCheck, Bell, ChevronRight, AlertTriangle } from "lucide-react";
import api, { fmtDay, fmtTime, formatErr } from "../../lib/api";
import { Loading, EmptyState, StatusPill } from "../../components/Shared";
import { AppHeader } from "../../components/native/AppShell";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

export function CmFamilies() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/cm/families").then((r) => setRows(r.data)).catch((e) => { toast.error(formatErr(e)); setRows([]); }); }, []);
  return (
    <div data-testid="cm-families-page">
      <AppHeader title="My Families" subtitle={rows ? `${rows.length} assigned` : ""} />
      <main className="mx-auto max-w-lg px-4 py-5">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="No families assigned" body="New assignments will appear here." /> : (
          <div className="space-y-3">
            {rows.map((r, i) => (
              <div key={r.parent?.id || i} className="p1-card flex items-center gap-3 p-4" data-testid={`cm-family-card-${i}`}>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-900 text-base font-bold text-white">{(r.parent?.name || "?")[0]}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-stone-900">{r.parent?.name}</p>
                  <p className="text-xs text-stone-500">{r.parent?.zone || "Jaipur"}{r.plan ? ` · ${r.plan.name}` : ""}</p>
                </div>
                <Link to="/app/cm/visits" className="rounded-full border border-stone-200 p-2 text-stone-400" data-testid={`cm-family-visits-${i}`}><ClipboardCheck className="h-4 w-4" /></Link>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export function CmVisits() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/cm/visits").then((r) => setRows(Array.isArray(r.data) ? r.data : r.data?.visits || [])).catch((e) => { toast.error(formatErr(e)); setRows([]); }); }, []);
  return (
    <div data-testid="cm-visits-page">
      <AppHeader title="Visits" subtitle="Scheduled and completed" />
      <main className="mx-auto max-w-lg px-4 py-5">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="No visits" body="Scheduled visits will appear here." /> : (
          <div className="space-y-2.5">
            {rows.map((v) => (
              <Link key={v.id} to={`/app/cm/visit/${v.id}`} className="p1-card flex items-center justify-between gap-3 p-4" data-testid={`cm-visit-row-${v.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-stone-900">{v.parent?.name || v.parent_name || "Visit"}</p>
                  <p className="text-xs text-stone-500">{fmtDay(v.scheduled_at)} · {fmtTime(v.scheduled_at)}{v.purpose ? ` · ${v.purpose}` : ""}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2"><StatusPill status={v.status} /><ChevronRight className="h-4 w-4 text-stone-300" /></div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export function CmAlerts() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/cm/today").then((r) => setD(r.data)).catch((e) => { toast.error(formatErr(e)); setD({ empty: true }); }); }, []);
  const incidents = d?.urgent_incidents || [];
  const requests = d?.open_requests || [];
  return (
    <div data-testid="cm-alerts-page">
      <AppHeader title="Alerts" subtitle={user?.name} />
      <main className="mx-auto max-w-lg space-y-5 px-4 py-5">
        {!d ? <Loading /> : (
          <>
            <section>
              <p className="p1-label flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> Urgent incidents</p>
              {incidents.length === 0 ? <p className="p1-card p-4 text-sm text-stone-400" data-testid="cm-alerts-empty">No open urgent incidents.</p> : (
                <div className="mt-2 space-y-2.5">
                  {incidents.map((i) => (
                    <div key={i.id} className="rounded-2xl border border-red-200 bg-red-50 p-4" data-testid={`cm-alert-${i.id}`}>
                      <p className="flex items-center justify-between text-sm font-bold text-red-800 capitalize">{i.category?.replace(/_/g, " ")} <StatusPill status={i.severity} /></p>
                      <p className="mt-1 text-xs text-red-700">{i.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
            <section>
              <p className="p1-label">Open family requests</p>
              {requests.length === 0 ? <p className="p1-card p-4 text-sm text-stone-400">No open requests from your families.</p> : (
                <div className="mt-2 space-y-2.5">
                  {requests.map((r) => (
                    <div key={r.id} className="p1-card flex items-center justify-between gap-3 p-4" data-testid={`cm-request-${r.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-stone-900">{r.type}</p>
                        <p className="text-xs text-stone-500">{fmtDay(r.created_at)}{r.details ? ` · ${r.details.slice(0, 50)}` : ""}</p>
                      </div>
                      <StatusPill status={r.status} />
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
